import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { Plus, X, Search } from "lucide-react";

const norm = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

export const SymptomSearch = ({ library, custom, selected, onPick, onCreated, onDeleted }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const blurRef = useRef(null);

  const nq = norm(q);
  const results = useMemo(() => {
    const base = library.map((name) => ({ name, id: null }));
    const all = [...custom.map((c) => ({ name: c.name, id: c.id })), ...base];
    return nq ? all.filter((s) => norm(s.name).includes(nq)).slice(0, 8) : all.slice(0, 8);
  }, [library, custom, nq]);
  const exact = [...library, ...custom.map((c) => c.name)].some((n) => norm(n) === nq);

  const pick = (name) => {
    onPick(name);
    setQ("");
    setOpen(false);
  };

  const create = async () => {
    if (nq.length < 2 || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/doctor/symptoms", { name: q.trim() });
      onCreated(data);
      pick(data.name);
      toast.success(`Síntoma «${data.name}» guardado en tu librería`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (e, s) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.delete(`/doctor/symptoms/${s.id}`);
      onDeleted(s);
      toast.success(`«${s.name}» eliminado de tu librería`);
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const inputRef = useRef(null);
  const submit = () => {
    if (nq.length < 2) return inputRef.current?.focus();
    const hit = results.find((r) => norm(r.name) === nq);
    if (hit) pick(hit.name);
    else create();
  };

  const onKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative mb-3" data-testid="symptom-search">
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-black/35 pointer-events-none" />
        <input
          ref={inputRef}
          data-testid="symptom-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => { clearTimeout(blurRef.current); setOpen(true); }}
          onClick={() => setOpen(true)}
          onBlur={() => { blurRef.current = setTimeout(() => setOpen(false), 150); }}
          onKeyDown={onKey}
          placeholder="Buscar o crear síntoma…"
          className="w-full h-10 pl-10 pr-28 rounded-full bg-white border border-black/[0.06] text-[13px] text-black placeholder:text-black/35 transition-[border-color,box-shadow]"
        />
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={submit}
          disabled={busy}
          data-testid="symptom-add-btn"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 px-3.5 rounded-full bg-[#0071E3] text-white text-[12px] font-semibold flex items-center gap-1 hover:bg-[#0062C4] active:scale-[0.97] transition-[background-color,transform] disabled:opacity-50"
        >
          <Plus className="w-3.5 h-3.5" />
          Agregar
        </button>
      </div>
      {open && (results.length > 0 || (nq.length >= 2 && !exact)) && (
        <div
          data-testid="symptom-search-dropdown"
          className="absolute z-30 left-0 right-0 mt-1.5 bg-white rounded-[18px] border border-black/[0.06] shadow-lg p-1.5 max-h-64 overflow-y-auto"
        >
          {results.map((s) => {
            const active = selected.has(s.name);
            return (
              <div
                key={s.id || s.name}
                role="button"
                tabIndex={-1}
                onMouseDown={(e) => { e.preventDefault(); pick(s.name); }}
                data-testid={`symptom-option-${norm(s.name).replace(/\s+/g, "-")}`}
                className="group flex items-center justify-between px-3 h-9 rounded-xl text-[13px] cursor-pointer hover:bg-black/[0.04] transition-colors"
              >
                <span className={active ? "text-[#0071E3] font-semibold" : "text-black/80"}>
                  {s.name}
                  {s.id && <span className="ml-2 text-[10px] uppercase tracking-widest text-black/30">Mío</span>}
                </span>
                {s.id && (
                  <button
                    type="button"
                    onMouseDown={(e) => remove(e, s)}
                    data-testid={`symptom-delete-${s.id}`}
                    title="Eliminar de mi librería"
                    className="w-6 h-6 grid place-items-center rounded-full text-black/30 hover:text-black hover:bg-black/[0.06] transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
          {nq.length >= 2 && !exact && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); create(); }}
              disabled={busy}
              data-testid="symptom-create-btn"
              className="w-full flex items-center gap-2 px-3 h-9 rounded-xl text-[13px] font-semibold text-[#0071E3] hover:bg-[#0071E3]/[0.06] transition-colors disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Crear «{q.trim()}»
            </button>
          )}
        </div>
      )}
    </div>
  );
};
